import { useState, useCallback } from 'react';

interface ShareButtonProps {
  url: string;
  /** overlay = dark semi-transparent pill (inside card hover layers)
   *  outline  = icon-only bordered button (sidebar / standalone)
   *  inline   = text+icon button for flat contexts            */
  variant?: 'overlay' | 'outline' | 'inline';
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Legacy fallback
    const el = document.createElement('textarea');
    el.value = text;
    el.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
}

export default function ShareButton({ url, variant = 'outline' }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await copyToClipboard(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  }, [url]);

  /* ── Overlay variant ─────────────────────────────────────────── */
  if (variant === 'overlay') {
    return (
      <button
        onClick={handleShare}
        className={`flex items-center gap-1.5 px-3 py-2 backdrop-blur-sm text-xs font-semibold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap border ${
          copied
            ? 'bg-emerald-500/90 text-white border-emerald-400/50'
            : 'bg-white/20 text-white hover:bg-white/30 border-white/40'
        }`}
      >
        <i className={`text-xs transition-all ${copied ? 'ri-check-line' : 'ri-share-line'}`}></i>
        <span className="transition-all">{copied ? 'Copied!' : 'Share'}</span>
      </button>
    );
  }

  /* ── Outline variant (icon-only + tooltip) ───────────────────── */
  if (variant === 'outline') {
    return (
      <div className="relative group/sharebtn">
        <button
          onClick={handleShare}
          className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-200 cursor-pointer ${
            copied
              ? 'border-emerald-200 bg-emerald-50 text-emerald-500'
              : 'border-gray-200 text-gray-400 hover:border-violet-300 hover:text-violet-500 hover:bg-violet-50'
          }`}
          title={copied ? 'Copied!' : 'Share template'}
        >
          <i className={`text-base transition-all ${copied ? 'ri-check-line' : 'ri-share-line'}`}></i>
        </button>

        {/* Tooltip */}
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 pointer-events-none z-20"
          aria-hidden="true"
        >
          <div
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-150 ${
              copied
                ? 'opacity-100 scale-100 bg-emerald-600 text-white'
                : 'opacity-0 scale-95 group-hover/sharebtn:opacity-100 group-hover/sharebtn:scale-100 bg-gray-900 text-white'
            }`}
          >
            {copied ? 'Link copied!' : 'Share template'}
          </div>
          {/* Arrow */}
          <div
            className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: `4px solid ${copied ? '#059669' : '#111827'}`,
            }}
          />
        </div>
      </div>
    );
  }

  /* ── Inline variant (text + icon pill) ───────────────────────── */
  return (
    <button
      onClick={handleShare}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border ${
        copied
          ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-violet-50 hover:border-violet-300 hover:text-violet-600'
      }`}
    >
      <i className={`text-sm ${copied ? 'ri-check-line' : 'ri-share-line'}`}></i>
      {copied ? 'Link copied!' : 'Share'}
    </button>
  );
}
